import { submitLead } from "../../forms/submit-lead";
import React from "react";
import { Menu, X, ChevronDown, CheckCircle2, XCircle } from "lucide-react";
import { getBusinessInitials } from "../../lib/business-initials";
import { formatPhoneDisplay, formatPhoneTel } from "../../lib/phone-formatter";
import { cn } from "../../lib/utils";
import { type BaseComponentProps } from "../../types/puck";
import { getWindowJobsiteConfig } from "../../types/runtime-config";
import { SmartLink } from "../smart-link";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

export interface NavigationLink {
  label: string;
  linkType: "page" | "url" | "anchor" | "dropdown";
  pageId?: string;
  url?: string;
  children?: NavigationLink[];
}

export interface NavigationProps extends BaseComponentProps {
  logo?: string;
  /**
   * Logo used while a transparent header sits over the hero photo. A one-colour mark that
   * reads on a light page usually disappears against photography, so the transparent state
   * needs its own file. Absent, the normal logo is used in both states.
   */
  logoOnDark?: string;
  logoText?: string;
  logoDisplay?: "logo" | "text" | "both";
  showLogoText?: "yes" | "no";
  links: NavigationLink[];
  style?: "transparent" | "solid" | "sticky";
  /** Human-saved nav: links render exactly as stored (no implicit "/" filter). */
  manuallyManaged?: boolean;
  /**
   * Transparent style only. "auto" keeps the call cluster quiet over the hero
   * and lets it fill with the brand colour once the header solidifies; "solid"
   * keeps the brand button over photography too.
   */
  ctaStyle?: "auto" | "solid";
  /** Transparent style only: link/brand color while the header sits over the hero photo. */
  transparentText?: "light" | "dark";
  variant?: "balanced" | "compact" | "accented";
  showPhone?: "yes" | "no";
  phone?: string;
  showTextUs?: "yes" | "no";
  showMonogram?: "yes" | "no";
  monogramText?: string;
  /**
   * Monogram colour. "brand" fills with the theme primary; "light" and "dark" are the
   * neutral pair, for logos-as-initials that shouldn't take the brand colour.
   */
  monogramStyle?: "brand" | "light" | "dark";
}

export const Navigation: React.FC<NavigationProps> = ({
  logo,
  logoOnDark,
  logoText,
  logoDisplay = "logo",
  showLogoText,
  links,
  style = "solid",
  manuallyManaged = false,
  ctaStyle = "auto",
  transparentText = "light",
  variant = "balanced",
  showPhone,
  phone,
  showTextUs = "yes",
  showMonogram = "yes",
  monogramText,
  monogramStyle = "brand",
  id,
  className,
}) => {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [expandedDropdown, setExpandedDropdown] = React.useState<number | null>(null);
  const [showTextDialog, setShowTextDialog] = React.useState(false);
  const [formState, setFormState] = React.useState<"idle" | "submitting" | "success" | "error">(
    "idle",
  );
  const [formData, setFormData] = React.useState({ name: "", phone: "", message: "" });

  React.useEffect(() => {
    if (style !== "sticky" && style !== "transparent") return;
    const handleScroll = () => setIsScrolled(window.scrollY > 16);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [style]);

  React.useEffect(() => {
    if (!isMobileMenuOpen) setExpandedDropdown(null);
  }, [isMobileMenuOpen]);

  // Auto-open SMS dialog when ?text=open is in the URL
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("sms") && showTextUs !== "no") {
      setShowTextDialog(true);
    }
  }, [showTextUs]);

  // Defensive: tolerate legacy/bad data where `logo` was stored as an object instead of a URL.
  const safeLogoUrl =
    typeof logo === "string" && (logo.startsWith("http") || logo.startsWith("/")) ? logo : "";
  const [logoLoadFailed, setLogoLoadFailed] = React.useState(false);
  React.useEffect(() => {
    setLogoLoadFailed(false);
  }, [safeLogoUrl]);
  const hasUsableLogo = Boolean(safeLogoUrl) && !logoLoadFailed;

  const safeLogoOnDarkUrl =
    typeof logoOnDark === "string" && (logoOnDark.startsWith("http") || logoOnDark.startsWith("/"))
      ? logoOnDark
      : "";
  const [logoOnDarkLoadFailed, setLogoOnDarkLoadFailed] = React.useState(false);
  React.useEffect(() => {
    setLogoOnDarkLoadFailed(false);
  }, [safeLogoOnDarkUrl]);
  const hasDarkSurfaceLogo = Boolean(safeLogoOnDarkUrl) && !logoOnDarkLoadFailed;

  const resolvedLogoDisplay = React.useMemo(() => {
    if (logoDisplay) return logoDisplay;
    if (!hasUsableLogo) return "text";
    if (showLogoText === "yes") return "both";
    if (showLogoText === "no") return "logo";
    return "logo";
  }, [hasUsableLogo, logoDisplay, showLogoText]);

  const brandName = (logoText || "").trim();
  const brandInitials = monogramText?.trim() || getBusinessInitials(brandName);
  // The logo already links home, so generated navs drop a "Home" item (url
  // "/") as redundant. A human-managed nav renders exactly what its owner
  // saved — if they put Home in, Home shows.
  const navLinks = Array.isArray(links)
    ? manuallyManaged
      ? links
      : links.filter((link) => link.url !== "/")
    : [];
  const formattedPhone = formatPhoneDisplay(phone);
  const telHref = phone ? `tel:${formatPhoneTel(phone)}` : undefined;
  const hasPhone = Boolean(phone);
  const showPhoneLink = showPhone === "yes" && hasPhone;
  const showTextUsButton = showTextUs !== "no" && hasPhone;
  const showLogo = hasUsableLogo && resolvedLogoDisplay !== "text";
  const showBrandText = Boolean(
    brandName &&
      (resolvedLogoDisplay === "text" || resolvedLogoDisplay === "both" || !hasUsableLogo),
  );
  const surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const;
  const getUrl = (link: NavigationLink) => link.url || "#";

  // --- Form ---
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    if (!formEl.checkValidity()) {
      formEl.reportValidity();
      return;
    }
    setFormState("submitting");
    try {
      const config = getWindowJobsiteConfig();
      if (config?.apiUrl && config?.siteId) {
        const params = new URLSearchParams(window.location.search);
        const isPreview = params.get("preview") === "true";
        const sessionId = isPreview ? null : sessionStorage.getItem("sp_session_id") || null;
        const result = await submitLead({
          apiUrl: config.apiUrl,
          siteId: config.siteId,
          backupApiUrl: config.backupApiUrl,
          businessName: config.business?.name,
          payload: {
            ...formData,
            formType: "quick-quote",
            channel: "text",
            sessionId,
          },
        });
        if (result.status === "failed") {
          throw new Error("Lead submission failed");
        }
      }
      setFormState("success");
      setFormData({ name: "", phone: "", message: "" });
      window.setTimeout(() => {
        setFormState("idle");
        setShowTextDialog(false);
      }, 3000);
    } catch {
      setFormState("error");
      window.setTimeout(() => setFormState("idle"), 3000);
    }
  };

  // --- Styles ---
  // A fully transparent surface can't back an open mobile menu, so the header
  // turns solid while the menu is out.
  const isTransparent = style === "transparent" && !isScrolled && !isMobileMenuOpen;
  const darkOnPhoto = transparentText === "dark";
  // The call cluster is the one control that may opt out of the transparent
  // treatment: on a phone-first business the number is the conversion, so it
  // can keep the brand button over the photo instead of going quiet.
  const ctaOverPhoto = isTransparent && ctaStyle !== "solid";
  // White-on-photo has no contrast guarantee — the hero image changes per
  // business and per slide — so the quiet treatment carries its own scrim,
  // the same translucent backdrop the on-photo badge tone uses.
  const ctaScrimClass = darkOnPhoto
    ? "jobsite:bg-white/25 jobsite:backdrop-blur-sm"
    : "jobsite:bg-black/20 jobsite:backdrop-blur-sm";

  // Over a transparent header the brand fill can vanish into the photo, so that state keeps
  // its translucent treatment whichever style is chosen.
  const monogramSurfaceClass = isTransparent
    ? darkOnPhoto
      ? "jobsite:bg-black/10 jobsite:text-neutral-900 jobsite:backdrop-blur"
      : "jobsite:bg-white/15 jobsite:text-white jobsite:backdrop-blur"
    : monogramStyle === "light"
      ? "jobsite:bg-muted jobsite:text-foreground"
      : monogramStyle === "dark"
        ? "jobsite:bg-foreground jobsite:text-background"
        : "jobsite:bg-primary jobsite:text-primary-foreground";
  const surfaceClass = cn(
    // Truly transparent while over the hero: no tint, no blur, no border — the hero's own
    // top scrim carries legibility. A frosted band here read as "not fully transparent".
    isTransparent
      ? "jobsite:border-b jobsite:border-transparent jobsite:bg-transparent"
      : "jobsite:border-b jobsite:border-border",
    !isTransparent && variant === "balanced" && "jobsite:bg-background/95 jobsite:backdrop-blur-md",
    !isTransparent && variant === "compact" && "jobsite:bg-background",
    !isTransparent &&
      variant === "accented" &&
      "jobsite:bg-linear-to-r jobsite:from-background jobsite:via-background jobsite:to-primary/5",
  );
  const brandTextClass = cn(
    "jobsite:min-w-0 jobsite:font-heading jobsite:tracking-tight",
    variant === "compact"
      ? "jobsite:text-base jobsite:sm:text-lg"
      : "jobsite:text-lg jobsite:sm:text-xl",
    isTransparent
      ? darkOnPhoto
        ? "jobsite:text-neutral-900"
        : "jobsite:text-white"
      : variant === "accented"
        ? "jobsite:text-primary"
        : "jobsite:text-foreground",
    // One line that shrinks against the nav links and ends in an ellipsis. The previous
    // two-line clamp keyed off brandName.length, which is blind to how wide the chosen
    // font actually renders, and its tight leading sheared the descenders off g/y/p.
    "jobsite:overflow-hidden jobsite:text-ellipsis jobsite:whitespace-nowrap",
  );
  const desktopLinkClass = cn(
    "jobsite:px-3 jobsite:py-2 jobsite:text-sm jobsite:font-medium jobsite:transition-colors jobsite:whitespace-nowrap jobsite:rounded-md",
    isTransparent
      ? darkOnPhoto
        ? "jobsite:text-neutral-900/80 jobsite:hover:text-neutral-900 jobsite:hover:bg-black/10"
        : "jobsite:text-white/90 jobsite:hover:text-white jobsite:hover:bg-white/10"
      : "jobsite:text-foreground/70 jobsite:hover:text-foreground jobsite:hover:bg-muted",
  );
  const dropdownTriggerClass = cn(
    desktopLinkClass,
    "jobsite:inline-flex jobsite:items-center jobsite:gap-1",
  );
  const mobileLinkClass = cn(
    "jobsite:flex jobsite:w-full jobsite:items-center jobsite:justify-between jobsite:px-4 jobsite:py-3 jobsite:text-left jobsite:text-base jobsite:font-medium jobsite:transition-colors",
    isTransparent
      ? darkOnPhoto
        ? "jobsite:text-neutral-900/95 jobsite:hover:bg-black/10"
        : "jobsite:text-white/95 jobsite:hover:bg-white/10"
      : "jobsite:text-foreground jobsite:hover:bg-muted",
  );

  return (
    <>
      <nav
        id={id}
        className={cn(
          "jobsite:z-50 jobsite:transition-all jobsite:duration-300",
          style === "sticky" && "jobsite:sticky jobsite:top-0",
          // Transparent has to leave the flow, or it reserves a strip of page above the hero
          // and the photo never reaches the top of the screen — which is the whole point of a
          // transparent header. It stays fixed as it fades to solid on scroll.
          style === "transparent"
            ? "jobsite:fixed jobsite:inset-x-0 jobsite:top-0"
            : "jobsite:relative",
          surfaceClass,
          isScrolled && "jobsite:shadow-md",
          className,
        )}
      >
        <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
          <div className="jobsite:flex jobsite:items-center jobsite:justify-between jobsite:gap-4 jobsite:h-16">
            {/* Brand */}
            <SmartLink
              to="/"
              className="jobsite:flex jobsite:min-w-0 jobsite:overflow-hidden jobsite:items-center jobsite:gap-3"
              aria-label={brandName || "Home"}
            >
              {showLogo ? (
                // Both logos are stacked and crossfaded rather than swapped by src, so the
                // change is as smooth as the header's own colour transition and never flashes
                // an empty box while the second file loads.
                <span
                  className={cn(
                    "jobsite:relative jobsite:block jobsite:shrink-0",
                    variant === "compact" ? "jobsite:h-8" : "jobsite:h-9",
                  )}
                >
                  <img
                    crossOrigin="anonymous"
                    src={safeLogoUrl}
                    alt={logoText || "Logo"}
                    onError={() => setLogoLoadFailed(true)}
                    className={cn(
                      "jobsite:h-full jobsite:w-auto jobsite:object-contain jobsite:transition-opacity jobsite:duration-300",
                      hasDarkSurfaceLogo && isTransparent && !darkOnPhoto && "jobsite:opacity-0",
                    )}
                  />
                  {hasDarkSurfaceLogo && (
                    <img
                      crossOrigin="anonymous"
                      src={safeLogoOnDarkUrl}
                      alt=""
                      aria-hidden="true"
                      onError={() => setLogoOnDarkLoadFailed(true)}
                      className={cn(
                        "jobsite:absolute jobsite:inset-0 jobsite:h-full jobsite:w-auto jobsite:object-contain jobsite:transition-opacity jobsite:duration-300",
                        isTransparent && !darkOnPhoto ? "jobsite:opacity-100" : "jobsite:opacity-0",
                      )}
                    />
                  )}
                </span>
              ) : showMonogram === "yes" && brandInitials ? (
                <span
                  className={cn(
                    "jobsite:flex jobsite:shrink-0 jobsite:items-center jobsite:justify-center jobsite:rounded-lg jobsite:font-heading",
                    // A square box only fits two characters. Three needs to grow sideways,
                    // so the width becomes a floor and padding does the rest.
                    variant === "compact"
                      ? "jobsite:h-8 jobsite:min-w-8 jobsite:text-sm"
                      : "jobsite:h-9 jobsite:min-w-9 jobsite:text-sm",
                    brandInitials.length > 2 ? "jobsite:px-2 jobsite:tracking-tight" : "",
                    monogramSurfaceClass,
                  )}
                  aria-hidden="true"
                >
                  {brandInitials}
                </span>
              ) : null}
              {showBrandText && (
                <span className={brandTextClass} title={brandName}>
                  {brandName}
                </span>
              )}
            </SmartLink>

            {/* Desktop nav links */}
            <div className="jobsite:hidden jobsite:shrink-0 jobsite:items-center jobsite:gap-1 jobsite:lg:flex">
              {navLinks.map((link, idx) =>
                link.linkType === "dropdown" && link.children?.length ? (
                  <div key={idx} className="jobsite:relative jobsite:group">
                    <SmartLink to={getUrl(link)} className={dropdownTriggerClass}>
                      {link.label}
                      <ChevronDown className="jobsite:w-3.5 jobsite:h-3.5 jobsite:opacity-50 jobsite:transition-transform jobsite:group-hover:rotate-180" />
                    </SmartLink>
                    <div className="jobsite:absolute jobsite:left-0 jobsite:top-full jobsite:pt-2 jobsite:opacity-0 jobsite:invisible jobsite:group-hover:opacity-100 jobsite:group-hover:visible jobsite:group-focus-within:opacity-100 jobsite:group-focus-within:visible jobsite:transition-all jobsite:duration-200">
                      <div
                        className="jobsite:min-w-[200px] jobsite:border jobsite:border-border jobsite:bg-background jobsite:p-1.5 jobsite:shadow-xl"
                        style={surfaceRadiusStyle}
                      >
                        {/* Drop the child that links back to the dropdown's own
                            page — the trigger already navigates there, so it
                            reads as a redundant duplicate (e.g. "Services"
                            inside the Services menu). Mobile keeps it: its
                            trigger only expands, so that child is the only way
                            to reach the index page there. */}
                        {link.children
                          .filter((child) => getUrl(child) !== getUrl(link))
                          .map((child, childIdx) => (
                            <SmartLink
                              key={childIdx}
                              to={getUrl(child)}
                              className="jobsite:block jobsite:px-3 jobsite:py-2 jobsite:text-sm jobsite:font-medium jobsite:text-foreground jobsite:transition-colors jobsite:hover:bg-muted"
                              style={surfaceRadiusStyle}
                            >
                              {child.label}
                            </SmartLink>
                          ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <SmartLink key={idx} to={getUrl(link)} className={desktopLinkClass}>
                    {link.label}
                  </SmartLink>
                ),
              )}
            </div>

            {/* Right side: Text Us | Phone | Hamburger */}
            <div className="jobsite:flex jobsite:shrink-0 jobsite:items-center jobsite:gap-3">
              {/* Desktop: "Text Us | (555) 123-4567" as a single unit */}
              {(showTextUsButton || showPhoneLink) && telHref && (
                <div
                  className={cn(
                    "jobsite:hidden jobsite:lg:flex jobsite:shrink-0 jobsite:items-center jobsite:whitespace-nowrap jobsite:text-sm jobsite:font-semibold jobsite:border jobsite:overflow-hidden",
                    ctaOverPhoto
                      ? cn(
                          ctaScrimClass,
                          darkOnPhoto
                            ? "jobsite:border-black/20 jobsite:text-neutral-900"
                            : "jobsite:border-white/25 jobsite:text-white",
                        )
                      : "jobsite:border-border jobsite:text-foreground",
                    // Solid over photography needs its own opaque surface, or
                    // foreground-coloured text sits on the image unreadably.
                    isTransparent && !ctaOverPhoto && "jobsite:bg-background",
                  )}
                  style={surfaceRadiusStyle}
                >
                  {showTextUsButton && (
                    <a
                      href="?sms=true"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowTextDialog(true);
                        window.history.replaceState(
                          null,
                          "",
                          `${window.location.pathname}?sms=true`,
                        );
                      }}
                      className={cn(
                        "jobsite:px-4 jobsite:py-2.5 jobsite:transition-colors",
                        ctaOverPhoto
                          ? darkOnPhoto
                            ? "jobsite:hover:bg-black/10"
                            : "jobsite:hover:bg-white/10"
                          : "jobsite:bg-primary jobsite:text-primary-foreground jobsite:hover:bg-primary/90",
                      )}
                    >
                      Text Us
                    </a>
                  )}
                  {showTextUsButton && showPhoneLink && (
                    <span
                      className={cn(
                        "jobsite:w-px jobsite:self-stretch",
                        ctaOverPhoto
                          ? darkOnPhoto
                            ? "jobsite:bg-black/20"
                            : "jobsite:bg-white/25"
                          : "jobsite:bg-border",
                      )}
                    />
                  )}
                  {showPhoneLink && (
                    <SmartLink
                      to={telHref}
                      className={cn(
                        "jobsite:px-4 jobsite:py-2.5 jobsite:transition-colors jobsite:tabular-nums",
                        ctaOverPhoto
                          ? darkOnPhoto
                            ? "jobsite:hover:bg-black/10"
                            : "jobsite:hover:bg-white/10"
                          : "jobsite:hover:bg-muted",
                      )}
                    >
                      {formattedPhone}
                    </SmartLink>
                  )}
                </div>
              )}

              {/* Mobile: compact call button */}
              {showPhoneLink && telHref && (
                <SmartLink
                  to={telHref}
                  className={cn(
                    "jobsite:flex jobsite:items-center jobsite:justify-center jobsite:h-10 jobsite:px-3 jobsite:text-sm jobsite:font-semibold jobsite:border jobsite:transition-colors jobsite:lg:hidden",
                    ctaOverPhoto
                      ? cn(
                          ctaScrimClass,
                          darkOnPhoto
                            ? "jobsite:border-black/20 jobsite:text-neutral-900 jobsite:hover:bg-black/10"
                            : "jobsite:border-white/25 jobsite:text-white jobsite:hover:bg-white/10",
                        )
                      : "jobsite:border-border jobsite:text-foreground jobsite:hover:bg-muted",
                    isTransparent && !ctaOverPhoto && "jobsite:bg-background",
                  )}
                  style={surfaceRadiusStyle}
                  aria-label={`Call ${formattedPhone}`}
                >
                  Call
                </SmartLink>
              )}

              {/* Mobile hamburger */}
              <button
                type="button"
                className={cn(
                  "jobsite:flex jobsite:h-10 jobsite:w-10 jobsite:items-center jobsite:justify-center jobsite:border jobsite:transition-colors jobsite:lg:hidden",
                  isTransparent
                    ? darkOnPhoto
                      ? "jobsite:border-black/15 jobsite:text-neutral-900 jobsite:hover:bg-black/10"
                      : "jobsite:border-white/15 jobsite:text-white jobsite:hover:bg-white/10"
                    : "jobsite:border-border jobsite:text-foreground jobsite:hover:bg-muted",
                )}
                style={surfaceRadiusStyle}
                onClick={() => setIsMobileMenuOpen((open) => !open)}
                aria-expanded={isMobileMenuOpen}
                aria-controls={id ? `${id}-mobile-menu` : "navigation-mobile-menu"}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMobileMenuOpen ? (
                  <X className="jobsite:w-5 jobsite:h-5" />
                ) : (
                  <Menu className="jobsite:w-5 jobsite:h-5" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {isMobileMenuOpen && (
            <div
              id={id ? `${id}-mobile-menu` : "navigation-mobile-menu"}
              className={cn(
                "jobsite:lg:hidden jobsite:border-t jobsite:py-4",
                isTransparent
                  ? darkOnPhoto
                    ? "jobsite:border-black/10"
                    : "jobsite:border-white/10"
                  : "jobsite:border-border",
              )}
            >
              <div className="jobsite:flex jobsite:flex-col jobsite:gap-2">
                {/* Text Us + Call Now */}
                {(showTextUsButton || showPhoneLink) && telHref && (
                  <div
                    className={cn(
                      "jobsite:grid jobsite:gap-2 jobsite:pb-3",
                      showTextUsButton && showPhoneLink
                        ? "jobsite:grid-cols-2"
                        : "jobsite:grid-cols-1",
                    )}
                  >
                    {showTextUsButton && (
                      <Button
                        asChild
                        variant="default"
                        className="jobsite:h-11 jobsite:font-semibold"
                      >
                        <a
                          href="?sms=true"
                          onClick={(e) => {
                            e.preventDefault();
                            setIsMobileMenuOpen(false);
                            setShowTextDialog(true);
                            window.history.replaceState(
                              null,
                              "",
                              `${window.location.pathname}?sms=true`,
                            );
                          }}
                        >
                          Text Us
                        </a>
                      </Button>
                    )}
                    {showPhoneLink && (
                      <Button
                        asChild
                        variant="secondary"
                        className="jobsite:h-11 jobsite:font-semibold"
                      >
                        <SmartLink to={telHref}>Call Now</SmartLink>
                      </Button>
                    )}
                  </div>
                )}

                {navLinks.map((link, idx) =>
                  link.linkType === "dropdown" && link.children?.length ? (
                    <div
                      key={idx}
                      className="jobsite:border jobsite:border-border/70 jobsite:bg-background/70 jobsite:p-1"
                      style={surfaceRadiusStyle}
                    >
                      <button
                        type="button"
                        onClick={() => setExpandedDropdown((c) => (c === idx ? null : idx))}
                        className={mobileLinkClass}
                        style={surfaceRadiusStyle}
                        aria-expanded={expandedDropdown === idx}
                      >
                        <span>{link.label}</span>
                        <ChevronDown
                          className={cn(
                            "jobsite:w-4 jobsite:h-4 jobsite:transition-transform",
                            expandedDropdown === idx && "jobsite:rotate-180",
                          )}
                        />
                      </button>
                      {expandedDropdown === idx && (
                        <div className="jobsite:space-y-1 jobsite:px-2 jobsite:pb-2">
                          {link.children.map((child, childIdx) => (
                            <SmartLink
                              key={childIdx}
                              to={getUrl(child)}
                              className="jobsite:block jobsite:px-4 jobsite:py-2.5 jobsite:text-sm jobsite:font-medium jobsite:text-muted-foreground jobsite:transition-colors jobsite:hover:bg-muted jobsite:hover:text-foreground"
                              style={surfaceRadiusStyle}
                              onClick={() => setIsMobileMenuOpen(false)}
                            >
                              {child.label}
                            </SmartLink>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <SmartLink
                      key={idx}
                      to={getUrl(link)}
                      className={cn(
                        mobileLinkClass,
                        "jobsite:border jobsite:border-border/70 jobsite:bg-background/70",
                      )}
                      style={surfaceRadiusStyle}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span>{link.label}</span>
                    </SmartLink>
                  ),
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Text Us Dialog */}
      <Dialog open={showTextDialog} onOpenChange={setShowTextDialog}>
        <DialogContent className="jobsite:sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="jobsite:font-heading jobsite:text-2xl">
              Send Us a Text
            </DialogTitle>
            <DialogDescription>We usually respond via text within a few minutes.</DialogDescription>
          </DialogHeader>

          {formState === "success" ? (
            <div className="jobsite:flex jobsite:flex-col jobsite:items-center jobsite:gap-3 jobsite:py-6 jobsite:text-center">
              <CheckCircle2 className="jobsite:h-12 jobsite:w-12 jobsite:text-emerald-500" />
              <p className="jobsite:text-lg jobsite:font-semibold jobsite:text-foreground">
                Message sent!
              </p>
              <p className="jobsite:text-sm jobsite:text-muted-foreground">
                We'll text you back shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} className="jobsite:space-y-4">
              <div className="jobsite:grid jobsite:gap-4 jobsite:sm:grid-cols-2">
                <Input
                  type="text"
                  name="name"
                  autoComplete="name"
                  placeholder="Your Name *"
                  required
                  value={formData.name}
                  onChange={handleFormChange}
                  disabled={formState === "submitting"}
                  className="jobsite:h-12 jobsite:text-base"
                />
                <Input
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="Phone Number *"
                  required
                  value={formData.phone}
                  onChange={handleFormChange}
                  disabled={formState === "submitting"}
                  className="jobsite:h-12 jobsite:text-base"
                />
              </div>
              <Textarea
                name="message"
                placeholder="How can we help? (optional)"
                rows={3}
                value={formData.message}
                onChange={handleFormChange}
                disabled={formState === "submitting"}
                className="jobsite:resize-none jobsite:text-base"
              />
              <Button
                type="submit"
                disabled={formState === "submitting"}
                className="jobsite:h-12 jobsite:w-full jobsite:font-semibold jobsite:text-base"
                size="lg"
              >
                {formState === "submitting" ? "Sending..." : "Send"}
              </Button>
              {formState === "error" && (
                <div className="jobsite:flex jobsite:items-center jobsite:gap-2 jobsite:text-sm jobsite:text-destructive">
                  <XCircle className="jobsite:h-4 jobsite:w-4 jobsite:shrink-0" />
                  <span>Something went wrong. Please try again.</span>
                </div>
              )}
              <p className="jobsite:text-[11px] jobsite:leading-relaxed jobsite:text-muted-foreground">
                By providing your phone number, you consent to receive calls and text messages
                (SMS). You can unsubscribe at any time by replying STOP. Message and data rates may
                apply.
              </p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
